import { type ReactNode, useState } from 'react';
import {
  MsalProvider,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react';
import { type IPublicClientApplication } from '@azure/msal-browser';
import {
  FluentProvider,
  Button,
  Title1,
  Body1,
  Caption1,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { loginRequest } from './authConfig';
import { AccessGate } from '@/components/AccessGate';
import { AppProvider } from '@/store/appContext';
import { brand, glass } from '@/theme/prismTheme';
import { ThemeModeProvider, useThemeMode } from '@/theme/themeMode';

const useStyles = makeStyles({
  loginPage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    gap: '24px',
    padding: '16px',
    position: 'relative' as const,
    overflow: 'hidden' as const,
    backgroundColor: '#FAFBFC',
    backgroundImage: brand.aurora,
  },
  loginCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
    padding: '40px 36px',
    background: glass.surfaceStrong.background,
    backdropFilter: glass.surfaceStrong.backdropFilter,
    WebkitBackdropFilter: glass.surfaceStrong.WebkitBackdropFilter,
    border: glass.surfaceStrong.border,
    boxShadow: glass.surfaceStrong.boxShadow,
    borderRadius: '24px',
    maxWidth: '400px',
    width: '100%',
    zIndex: 1,
  },
  brandLogo: {
    height: '52px',
    width: 'auto',
    marginBottom: '4px',
  },
  appName: {
    background: `linear-gradient(135deg, ${brand.blueDeep} 0%, ${brand.blue} 60%, ${brand.teal} 100%)`,
    WebkitBackgroundClip: 'text' as const,
    WebkitTextFillColor: 'transparent' as const,
    backgroundClip: 'text' as const,
    fontWeight: 700,
    letterSpacing: '-1px',
  },
  subtitle: {
    textAlign: 'center' as const,
    color: tokens.colorNeutralForeground2,
  },
  signInBtn: {
    minWidth: '220px',
    background: `linear-gradient(135deg, ${brand.blueDeep} 0%, ${brand.blue} 100%)`,
    border: 'none',
    boxShadow: '0 4px 14px rgba(91,146,229,0.35)',
    '&:hover': {
      background: `linear-gradient(135deg, ${brand.blueDeep} 0%, ${brand.blueDeep} 100%)`,
      transform: 'translateY(-1px)',
    },
  },
  trustRow: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    marginTop: '4px',
    color: tokens.colorNeutralForeground3,
    fontSize: '11px',
  },
  trustDot: {
    width: '4px',
    height: '4px',
    borderRadius: '50%',
    background: tokens.colorNeutralStroke1,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    textAlign: 'center' as const,
    maxWidth: '360px',
  },
  resetLink: {
    marginTop: '8px',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    textDecoration: 'underline',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    fontFamily: 'inherit',
  },
});

/**
 * Redirect flow is used everywhere by default — it's the only reliable option in:
 *  - PWA standalone mode (no popup allowed)
 *  - Mobile browsers (pop-up blockers)
 *  - Safari (3rd-party cookie policy can block popup token exchange)
 *  - Corporate networks with strict popup policies
 *
 * Popup is faster but fragile. We make redirect the default to avoid loops.
 */
const USE_REDIRECT_FLOW = true;

function LoginPage() {
  const { instance } = useMsal();
  const styles = useStyles();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleLogin = async () => {
    setError(null);
    setBusy(true);
    try {
      if (USE_REDIRECT_FLOW) {
        await instance.loginRedirect(loginRequest);
        // page navigates away, no further code runs
      } else {
        await instance.loginPopup(loginRequest);
      }
    } catch (err) {
      console.error('[Prism] Login failed:', err);
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  const handleReset = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      // Clear MSAL cookies
      document.cookie.split(';').forEach((c) => {
        const name = c.split('=')[0].trim();
        if (name.startsWith('msal.'))
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      });
    } catch {
      /* ignore */
    }
    location.href = '/';
  };

  return (
    <div className={styles.loginPage}>
      <div className={styles.loginCard}>
        <img
          src="/brand/2sd-logo.png"
          alt="2SD Technologies"
          className={styles.brandLogo}
        />
        <Title1 className={styles.appName}>Prism</Title1>
        <Body1 className={styles.subtitle}>
          Resource & Cost Management
        </Body1>
        <Button
          appearance="primary"
          size="large"
          onClick={handleLogin}
          disabled={busy}
          className={styles.signInBtn}
        >
          {busy ? 'Redirecting to Microsoft…' : 'Sign in with Microsoft'}
        </Button>
        {error && (
          <Caption1 className={styles.error}>
            {error}
          </Caption1>
        )}
        <div className={styles.trustRow}>
          <span>SSO</span>
          <span className={styles.trustDot} />
          <span>MFA enforced</span>
          <span className={styles.trustDot} />
          <span>Biometric ready</span>
        </div>
      </div>
      <button className={styles.resetLink} onClick={handleReset}>
        Stuck in a loop? Clear session and reset.
      </button>
    </div>
  );
}

interface AuthProviderProps {
  msalInstance: IPublicClientApplication;
  children: ReactNode;
}

/**
 * Inner wrapper that consumes the theme mode and renders FluentProvider.
 * Has to be a child of ThemeModeProvider so useThemeMode() works.
 */
function ThemedRoot({ children }: { children: ReactNode }) {
  const { theme } = useThemeMode();
  return (
    <FluentProvider theme={theme}>
      <AuthenticatedTemplate>
        {/* CRITICAL: AppProvider MUST wrap AccessGate. AccessGate calls
            useAppContext() to read isInitialized; if AppProvider is
            elsewhere (e.g. inside `children`), the context defaults
            kick in and isInitialized is always false → infinite spinner. */}
        <AppProvider>
          <AccessGate>{children}</AccessGate>
        </AppProvider>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>
    </FluentProvider>
  );
}

export function AuthProvider({ msalInstance, children }: AuthProviderProps) {
  return (
    <MsalProvider instance={msalInstance}>
      <ThemeModeProvider>
        <ThemedRoot>{children}</ThemedRoot>
      </ThemeModeProvider>
    </MsalProvider>
  );
}
